import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../../config/apiConfig';

const Container = styled.div`
  position: relative;
  width: 100%;
  z-index: 100;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  /* Ensure scrolling works properly */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const CustomerName = styled.div`
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 2px;
`;

const CustomerDetails = styled.div`
  font-size: 12px;
  color: #7f8c8d;
`;

const CreateNewButton = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  background-color: #f0f9e7;
  color: #2d5a0b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #d4edaa;
  
  &:hover {
    background-color: #e4f5d4;
  }
`;

const SelectedCustomerCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const SelectedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const SelectedName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
`;

const ChangeButton = styled.button`
  background: none;
  border: none;
  color: #3498db;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SelectedDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const DetailItem = styled.div`
  font-size: 13px;
  color: #495057;
  
  span {
    color: #7f8c8d;
    margin-right: 4px;
  }
`;

const LoadingText = styled.div`
  padding: 16px;
  text-align: center;
  color: #7f8c8d;
  font-size: 14px;
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #7f8c8d;
  font-size: 14px;
`;

export function CustomerPicker({ onSelect, onCreateNew, selectedCustomer, onClear }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search customers with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length < 2) {
      setCustomers([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('admin-token');
        const response = await fetch(
          `${API_BASE_URL}/admin/customers/search?q=${encodeURIComponent(searchTerm)}&limit=10`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token
            }
          }
        );
        const result = await response.json();
        if (result.is_success) {
          setCustomers(result.data.customers || []);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const handleSelect = (customer) => {
    onSelect(customer);
    setSearchTerm('');
    setShowDropdown(false);
    setCustomers([]);
  };

  const handleCreateNew = () => {
    setShowDropdown(false);
    onCreateNew();
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  if (selectedCustomer) {
    return (
      <SelectedCustomerCard>
        <SelectedHeader>
          <SelectedName>
            {selectedCustomer.firstname} {selectedCustomer.lastname}
          </SelectedName>
          <ChangeButton onClick={handleClear}>Change Customer</ChangeButton>
        </SelectedHeader>
        <SelectedDetails>
          <DetailItem>
            <span>Email:</span> {selectedCustomer.email || '-'}
          </DetailItem>
          <DetailItem>
            <span>Phone:</span> {selectedCustomer.mobile || '-'}
          </DetailItem>
          <DetailItem>
            <span>Pincode:</span> {selectedCustomer.pincode || '-'}
          </DetailItem>
          {selectedCustomer.gst && (
            <DetailItem>
              <span>GST:</span> {selectedCustomer.gst}
            </DetailItem>
          )}
        </SelectedDetails>
        {selectedCustomer.address && (
          <DetailItem style={{ marginTop: '8px' }}>
            <span>Address:</span> {selectedCustomer.address}
          </DetailItem>
        )}
      </SelectedCustomerCard>
    );
  }

  return (
    <Container ref={containerRef}>
      <SearchInput
        type="text"
        placeholder="Search customers by name, email, phone..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
      />
      
      {showDropdown && (
        <Dropdown>
          {loading ? (
            <LoadingText>Searching...</LoadingText>
          ) : customers.length > 0 ? (
            <>
              {customers.map((customer) => (
                <DropdownItem key={customer.userId} onClick={() => handleSelect(customer)}>
                  <CustomerName>
                    {customer.firstname} {customer.lastname}
                  </CustomerName>
                  <CustomerDetails>
                    {customer.email} {customer.mobile && `• ${customer.mobile}`} {customer.pincode && `• ${customer.pincode}`}
                  </CustomerDetails>
                </DropdownItem>
              ))}
              <CreateNewButton onClick={handleCreateNew}>
                + Create New Customer
              </CreateNewButton>
            </>
          ) : searchTerm.length >= 2 ? (
            <>
              <NoResults>No customers found for "{searchTerm}"</NoResults>
              <CreateNewButton onClick={handleCreateNew}>
                + Create New Customer
              </CreateNewButton>
            </>
          ) : null}
        </Dropdown>
      )}
    </Container>
  );
}
